<script setup lang="ts">
const { t: $t } = useI18n()

import { createInput } from '@formkit/vue'
import AccessoryInput from '~/layers/customer-module/components/CustomerModuleMachinery/Creation/AccessoryInput.vue'
import type { CreateForeignMachineryAccessory, MachineryAccessoryCategory, MachineryRubrics } from '~/types'

const props = defineProps<{
  payload: {
    machineryId: string
    machineryRubric?: MachineryRubrics
  }
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const formData = ref<{
  machineryId: string
  accessories: CreateForeignMachineryAccessory[]
}>({ machineryId: props.payload.machineryId, accessories: [] })

const accessoryInput = createInput(AccessoryInput, {
  props: ['options'],
})

const rubricToAccessoryOptions: Partial<Record<MachineryRubrics, MachineryAccessoryCategory[]>> = {
  dieselForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  gasForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  allTerrainForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  fourWayForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  sideLoader: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  electricForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster', 'charger'],
  reachTruck: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster', 'charger'],
  telescopicForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
  rotatingForklift: ['fork', 'forkExtension', 'sideShifter', 'forkAdjuster'],
}
const accessoryOptions = computed(() => props.payload.machineryRubric ? (rubricToAccessoryOptions[props.payload.machineryRubric] ?? []) : [])

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const attachAccessories = useMutation({
  mutationFn: $trpc.machinery.attachMachineryAccessoryToForeignMachinery.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('foreignMachinery.accessoryAsignmentPopup.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
      queryClient.invalidateQueries({ queryKey: ['accessories'] }),
    ])
    emit('close')
    notification.success({ title: $t('foreignMachinery.accessoryAsignmentPopup.success'), duration: 4500 })
  },
})
</script>

<template>
  <ThePopup show :title="$t('foreignMachinery.accessoryAsignmentPopup.title', { machineryId: payload.machineryId })" @close="emit('close')">
    <p class="mb-2">
      {{ $t('foreignMachinery.accessoryAsignmentPopup.description') }}
    </p>
    <FormKit
      v-model="formData"
      type="form"
      @submit="attachAccessories.mutate(formData)"
    >
      <!-- @vue-expect-error: After upgrading to latest Vue 3.5 and TS 5.8 in #4136 inference became stricter, generating a new error for a working form, that had no explicit error before. -->
      <FormKit
        v-if="accessoryOptions.length > 0"
        name="accessories"
        :type="accessoryInput"
        :options="accessoryOptions"
      />
    </FormKit>
  </ThePopup>
</template>
